/**
 * The gateway.
 *
 * ── WHAT THIS PASS CHANGED, AND WHY ───────────────────────────────────────
 * Thomas rejected the previous build in one sentence: "This looks like a
 * wireframe not a finalized high fidelity EdTech subscription page for
 * universities." Three faults sat behind that, and the masthead pass fixed the
 * first two (an invisible logo, a hero pasted onto a white page). This pass
 * owns the third: everything BELOW the masthead was a white field with three
 * text blocks on it.
 *
 * Four moves, in the order a reader meets them:
 *
 *   1. THE PRICE CARD OVERLAPS THE MASTHEAD. The slider is the page's hero
 *      act, so it is lifted into the dark surface's bottom edge rather than
 *      starting 80px below it on cream. Two planes instead of two stacked
 *      rectangles, and the reader's eye lands on the instrument.
 *   2. THE GROUND IS WARM, NOT WHITE. The campus photograph P3 already runs
 *      behind the member app washes the top of the light body at a tenth of
 *      its opacity, and the audience band is a full-bleed inset ground, so
 *      the page has three surfaces rather than one.
 *   3. PHOTOGRAPHY WHERE IT REPLACES TEXT. "Who this is for" is three of
 *      P3's own photographs with ten words each, which is what a wall of
 *      prose about audiences was.
 *   4. THE PARTNER WALL. Six organizations P3 actually works with, labelled
 *      for exactly what they are, because none of them pays for a
 *      subscription and a logo strip that implied otherwise is the failure
 *      this whole surface exists to avoid.
 *
 * ── WHAT MOVED OFF THIS PAGE ──────────────────────────────────────────────
 * "What we do not do" is gone from here, at Thomas's direct instruction. All
 * thirteen absences, the subprocessor list, data residency and the k-anonymity
 * commitment now live on /trust as designed sections rather than as a tabbed
 * list on a pricing page. One line here links to them, so a procurement
 * officer still reaches every item in two clicks, which is the standard they
 * are entitled to.
 *
 * MENTOR SUPPLY AND THE NO-MATCH GUARANTEE STAYED. Those are a promise about
 * what the buyer is getting, not a compliance disclosure, so they belong
 * beside the price and they are the last thing read before the call to
 * action.
 *
 * ── THE TEXT BUDGET IS UNCHANGED ──────────────────────────────────────────
 * Adobe sells a $70 a month subscription in 211 words of visible copy. Adding
 * visual richness was not allowed to buy back text, so every card added in
 * this pass carries at most twelve words and no paragraph runs past two
 * lines. tests/pricing.test.ts enforces the ceiling on every string that
 * lives in pricing.ts.
 *
 * ── WHY THE PAGE HOLDS THE STATE AND THE SLIDER DOES NOT ──────────────────
 * The hero's stat row, the price panel, the two calls to action and the
 * printed sentence all read the same band. One owner, four readers. A slider
 * that owned its own band would need three callbacks out of it and the hero
 * would lag the panel by a render on every step.
 */

import { useState } from "react";

import { CapacitySlider, type Billing } from "~/app/components/CapacitySlider";
import { GatewayHero, HeroAccent } from "~/app/components/GatewayHero";
import { GatewayShell } from "~/app/components/GatewayShell";
import { HowItWorks } from "~/app/components/HowItWorks";
import {
  SectionHead,
  SubGroupLabel,
  buttonClass,
} from "~/app/components/primitives";
import { cn } from "~/app/lib/cn";
import { formatCount, formatMoney } from "~/app/lib/format";
import {
  BANDS,
  DEFAULT_CAPACITY,
  ENTRY_BAND,
  GATEWAY_FAQ,
  GATEWAY_INCLUDED,
  LEGAL,
  MENTOR_SUPPLY,
  MENTOR_SUPPLY_SHORT,
  QUOTE_RESPONSE_BUSINESS_DAYS,
  STUDENT_SUITE,
  bandById,
  type CapacityChoice,
} from "~/app/lib/pricing";

export type CheckoutPath = "card" | "quote";

/* ==========================================================================
   The partner wall

   HOW THIS IS LABELLED IS THE WHOLE POINT. P3 has no paying institutional
   partner. A logo strip on a pricing page is read as a customer list by
   default, so the line under it says what these organizations are and says
   what they are not, in the same breath. Getting caught overstating this
   once would cost more than the strip is worth.
   ========================================================================== */

export interface PricingProps {
  /** Off when a host renders its own chrome. */
  chrome?: boolean;
  className?: string;
}

/* ==========================================================================
   THE REPOSITION, September 8 2026

   Thomas: "to scale properly I want to showcase that students can get ahead
   and find successful solutions for their career goals with P3 even without a
   mentor." This page used to open on "Institutional reporting, starting at
   $400 a month", which led with the weakest thing P3 owns and capped the
   product's value at mentor capacity. It now opens on what a student gets.

   Three consequences worth holding on to:
     · the price is still in the hero, in the sub rather than in the H1, so
       the marketing hook survives without being the first idea on the page;
     · "What is included" is two groups, students then institution, and the
       order is the argument;
     · the mentor-supply block did not soften. The numbers and the
       no-guarantee sentence are unchanged. Only the frame moved.

   The honesty gate is unmoved with it. Everything above describes what a
   STUDENT can do, which needs no cohort data to be true. It says nothing
   about what the institution will SEE, because the Sep 7 org-scoping probe
   measured milestone completions topping out at three distinct students and
   no named institution reaching ten monthly actives in August 2026, so most
   engagement panels would be suppressed for a real first cohort. Those are
   different claims and this page keeps them apart.
   ========================================================================== */

export function Pricing({ chrome = true, className }: PricingProps) {
  /* ONE piece of state for the whole purchase decision. It used to be two,
     `bandId` and `students`, and they could and did disagree: see the "ONE
     VALUE, TWO CONTROLS" block in pricing.ts. */
  const [choice, setChoice] = useState<CapacityChoice>(DEFAULT_CAPACITY);
  const [billing, setBilling] = useState<Billing>("monthly");

  const band = bandById(choice.bandId);
  const quoted = band.monthly === null;

  /* One label, one destination, three places on the page. Requirement 4 of
     the rebrief is that the route into a subscription looks like the same
     button in the masthead, at the price and at the close, so the string and
     the class both live here rather than being typed out three times. */
  const startLabel = quoted ? "Request a quote" : "Register";
  const startHref = checkoutHref(quoted ? "quote" : "card", choice);

  return (
    <GatewayShell
      current="pricing"
      chrome={chrome}
      mainId="pricing-main"
      skipLabel="Skip to the price"
      className={className}
    >
      {/* ================================================================
          The masthead. The nav sits inside this surface: GatewayHero paints
          it full bleed and lifts it up past the header.

          THE FOUR STAT ROW IS GONE, September 8 2026, at Thomas's
          instruction: "delete the stat row entirely so the slider is front
          and centre". It is a reversal of an earlier instruction and it is
          the right one. The stats (free accounts, 17 pathways, approved
          mentors, the reporting floor) were added when the hero had to carry
          the whole argument on its own; the slider carries it now, and every
          one of those four facts is still on the page, in the student list,
          in the mentorship card and on /trust. Four figures between the sub
          and the price only delayed the instrument.

          What is left is eyebrow, headline, sub, term, and the rhythm was
          re-measured rather than re-guessed after the cut. At 1440 the band
          is 362px tall where it was 515, the copy still opens 132px below the
          top of the document, 108px of dark still sits under the last line,
          the term line still clears the price card by 68px, and the card
          still crosses the surface's bottom arc by the full 40px of
          `--eg-lift`. Nothing needed moving: the four cells were between the
          sub and the price, so removing them closed a gap rather than opening
          one, and both ends of the band kept the spacing they were tuned to.

          The reassurance line is the hero's FOOTER rather than a sibling
          below it, because the price card overlaps the bottom of the dark
          surface and anything left on the cream between them would be run
          over by it.
          ================================================================ */}
      <section className="eg-hero">
        <GatewayHero
          className="has-lift"
          title={
            <>
              Opportunities, pathways and answers for{" "}
              <HeroAccent>every student</HeroAccent>.
            </>
          }
          sub={`Scholarships, internships, career pathways and answers from mentors, from the day they join. Reporting for you starts at ${formatMoney(ENTRY_BAND.monthly ?? 0)} a month.`}
        />
      </section>

      {/* ================================================================
          The price. Lifted into the masthead's bottom edge, which is the
          page's one deliberate overlap: the instrument reads as the object
          the whole surface was built to hold rather than as the next
          rectangle down.
          ================================================================ */}
      <section className="eg-sec is-lift" aria-labelledby="price-heading">
        <h2 id="price-heading" className="e-sr-only">
          Choose your capacity
        </h2>

        <CapacitySlider
          choice={choice}
          onChoiceChange={setChoice}
          billing={billing}
          onBillingChange={setBilling}
          actions={
            <>
              <a
                className={cn(buttonClass("primary", "lg"), "eg-cta-primary")}
                href={startHref}
              >
                {startLabel}
              </a>
              <p>
                <a className="eg-alt" href={`mailto:${LEGAL.contactEmail}`}>
                  Talk to us instead
                </a>
              </p>
            </>
          }
        />

        {/* The capacity sentence moved here when the hero was repositioned onto
            student value. It belongs against the instrument anyway: it is the
            answer to the question the slider itself provokes. */}

        {/* Only on the quoted band. On a priced band this line said the same
            thing as the reassurance in the hero, and saying the term twice on
            one screen is exactly the density the rebuild removed. */}
        {quoted ? (
          <p className="eg-subline">
            We return a written quote within {QUOTE_RESPONSE_BUSINESS_DAYS}{" "}
            business day.
          </p>
        ) : null}
      </section>

      {/* ================================================================
          What is included: ONE section, two columns, and the single line that
          carries a procurement reader to /trust.
          ================================================================ */}
      <section className="eg-sec" aria-labelledby="included-heading">
        <SectionHead
          id="included-heading"
          /* No eyebrow: "Included" over "What is included" was the heading
             said twice (Thomas, Sep 21 2026), and the Questions head below
             carries none either. */
          title="What is included"
          /* 13 words. The previous line ran 25 and rendered as THREE lines at
             390px, over the two line ceiling. Its second sentence also said
             what the two visible column labels below it already say, so the
             cut is redundancy, not information. */
          lede="Students get the first list from day one, with or without a mentor."
        />

        {/* ONE SECTION, TWO COLUMNS, September 8 2026. It used to be two
            stacked half-sections with a third block wedged between them, so a
            reader met three headings for one idea and the two lists never sat
            side by side, which is the only arrangement that lets them be
            compared.

            THE ORDER IS STILL THE ARGUMENT. Students on the left, admin on the
            right, because the thing an institution is buying is what happens
            to its students, and a page that opens on a dashboard sells the
            weakest thing P3 owns. */}
        <div className="eg-incl2">
          <div className="eg-incl-col">
            <SubGroupLabel id="incl-students">Students</SubGroupLabel>
            <div className="eg-incl" role="group" aria-labelledby="incl-students">
              {STUDENT_SUITE.map((row) =>
                row.title === "Mentorship" ? (
                  <MentorshipCard key={row.title} detail={row.detail} />
                ) : (
                  <div className="eg-incl-item" key={row.title}>
                    <h3>{row.title}</h3>
                    <p>{row.detail}</p>
                  </div>
                ),
              )}
            </div>
          </div>

          <div className="eg-incl-col">
            <SubGroupLabel id="incl-admin">Admin</SubGroupLabel>
            <div className="eg-incl" role="group" aria-labelledby="incl-admin">
              {GATEWAY_INCLUDED.map((row) => (
                <div className="eg-incl-item" key={row.title}>
                  <h3>{row.title}</h3>
                  <p>{row.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* The four steps, moved here from /overview (Thomas, Sep 21 2026).
            They belong under this heading rather than beside it: the two
            columns above say what an institution gets, these say how it
            starts. No label over them, because numbered steps titled "Choose a
            capacity" and "Share your join link" announce themselves, and a
            third heading inside one section is what this page was already
            being trimmed of. The rule above them is the separation the
            section boundary used to give. */}
        <div className="eg-incl-how">
          <HowItWorks />
        </div>
      </section>

      {/* Who this is for lives on /overview only (Thomas, Sep 9 2026): the
          same three photo cards one click apart read as repetition. */}

      {/* The partner wall lives on /overview only (Thomas, Sep 9 2026): the
          same strip on two pages one click apart read as padding. */}

      {/* ================================================================
          Questions, and the rate card for the reader who wants the table.
          The table is a disclosure rather than a section because the slider
          already shows every figure in it; this is for copying into a
          requisition.
          ================================================================ */}
      <section className="eg-sec eg-sec-faq" aria-labelledby="faq-heading">
        <SectionHead
          id="faq-heading"
          title="Questions"
        />
        <Faq />
      </section>

      {/* ================================================================
          The close. The same button, a third time, on the same dark surface
          the page opened with, so the page ends where it began.
          ================================================================ */}
      <section className="eg-close" aria-labelledby="close-heading">
        <span className="eg-close-bg" aria-hidden="true">
          <span className="eg-close-bloom" />
          <span className="eg-close-mark" />
        </span>
        <div className="eg-close-grid">
          <div className="eg-close-in">
            <p className="eg-band-eyebrow">Ready when you are</p>
            <h2 id="close-heading">
              Start with the band you can{" "}
              <span className="eg-accent">commit to</span>.
            </h2>
            <p className="eg-close-sub">
              Pay by card today, or request a quote and we send a W-9,
              remittance details and an invoice.
            </p>
            <div className="eg-close-actions">
              <a
                className={cn(buttonClass("primary", "lg"), "eg-cta-primary")}
                href={startHref}
              >
                {startLabel}
              </a>
              <a className="eg-close-alt" href={`mailto:${LEGAL.contactEmail}`}>
                Talk to us instead
              </a>
            </div>
          </div>

          {/* The fine print rides INSIDE the closing surface. It used to be
              its own cream section between the close and the dark footer,
              which put an 80px band of paper between two dark planes and read
              as a gap rather than as a page ending.

              IT CARRIES ONE SENTENCE NOW. It used to open with the entity,
              its status, its state of incorporation and the EIN, which is
              word for word what the footer says about 200px below it. That
              was half of the "double footer" Thomas called heavy: two blocks
              of supplier identity, stacked, on one screen. The footer keeps
              it, because that is where a procurement office looks for it.
              What is left here is the one thing a buyer needs at the moment
              they decide and cannot read anywhere else on the page. */}
          <div className="eg-close-fine">
            <p>
              Prices are in US dollars and exclude any tax your institution is
              required to withhold.
            </p>
          </div>
        </div>
      </section>
    </GatewayShell>
  );
}

/* ==========================================================================
   The mentorship card

   WHAT THIS REPLACES, AND WHAT IT IS NOT ALLOWED TO DROP.

   Until September 8 2026 the mentor-supply numbers lived in their own block,
   "Mentorship / The deepest pillar, counted in the open", wedged between the
   student list and the institution list. Thomas deleted the section. He did
   not delete the disclosure, and the difference is the whole point of this
   component: the three figures (154 approved mentors, 526 mentorships,
   counted September 7 2026) and the no-guarantee sentence are the one fact on
   this page that could genuinely embarrass P3 if a customer found it after
   signing rather than before. So they moved INTO the card, where mentorship
   is now the last of the seven things a student gets.

   The card is wider than its neighbours and it is the only one on the page
   with a figure strip, which is the right weight for the deepest pillar
   without giving it a section of its own again.
   ========================================================================== */

function MentorshipCard({ detail }: { detail: string }) {
  return (
    <div className="eg-incl-item is-deep">
      <h3>Mentorship</h3>
      <p>{detail}</p>
      <dl className="eg-supply">
        <div>
          <dt className="eg-supply-l">Approved mentors</dt>
          <dd className="eg-supply-v">
            {formatCount(MENTOR_SUPPLY.approvedMentors)}
          </dd>
        </div>
        <div>
          <dt className="eg-supply-l">Mentorships</dt>
          <dd className="eg-supply-v">
            {formatCount(MENTOR_SUPPLY.mentorships)}
          </dd>
        </div>
      </dl>
      {/* The date is a date, not a third count. It was a display figure
          beside the other two and read as one. */}
      <p className="eg-supply-count">Counted {MENTOR_SUPPLY.asOf}</p>
      <ul className="eg-promise-lines">
        {MENTOR_SUPPLY_SHORT.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

/* ==========================================================================
   FAQ
   ========================================================================== */

function Faq() {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="eg-faq">
      {GATEWAY_FAQ.map((item) => {
        const isOpen = open === item.q;
        const panelId = `faq-${slug(item.q)}`;
        return (
          <div className="eg-faq-item" key={item.q}>
            <h3>
              <button
                type="button"
                className="eg-faq-q"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpen(isOpen ? null : item.q)}
              >
                <span>{item.q}</span>
                <svg
                  className="eg-faq-sign"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M2.5 8h11" />
                  {isOpen ? null : <path d="M8 2.5v11" />}
                </svg>
              </button>
            </h3>
            <div className="eg-faq-a" id={panelId} hidden={!isOpen}>
              {item.a}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ==========================================================================
   Helpers
   ========================================================================== */

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * The checkout link carries the whole selection, so a buyer who opens it in a
 * new tab, bookmarks it, or mails it to their purchasing office arrives at
 * the same offer. Checkout re-reads every value through `bandFromWire` and
 * `parseStudentCount` rather than trusting the string.
 */
export function checkoutHref(path: CheckoutPath, choice: CapacityChoice): string {
  const params = new URLSearchParams({ path, band: choice.bandId });
  if (choice.students !== null) params.set("students", String(choice.students));
  return `/checkout?${params.toString()}`;
}

/** BANDS is re-exported here so a route file can build a static price list. */
export { BANDS };
